import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Star, ChevronDown, Play, Phone, Clock } from "lucide-react";
import { Reveal } from "../components/motion";
import { WorldMap } from "../components/WorldMap";
import { QuoteForm } from "../components/QuoteForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { BUSINESS, COUNTRIES, FAQS, PORTFOLIO } from "../data/content";

export const PortfolioPreview = ({ chapter = "05" }) => (
  <section className="py-24" data-testid="portfolio-preview">
    <div className="container-x">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow mb-3">{chapter} — Selected work</p>
          <h2>Concept work, presented honestly.</h2>
          <p className="text-[#A0A3C2] mt-4 max-w-xl m-0">
            Demo concept pieces showing our design and video direction — real client work will replace these as projects ship.
          </p>
        </div>
        <Link to="/portfolio" data-testid="portfolio-preview-link" className="btn btn-ghost no-underline">
          Full portfolio <ArrowRight size={16} />
        </Link>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
        {PORTFOLIO.slice(0, 6).map((p, i) => (
          <Reveal key={p.title} delay={(i % 3) * 0.1}>
            <Link to="/portfolio" data-testid={`portfolio-item-${i}`} className="card overflow-hidden block no-underline text-[#F5F5FA] group">
              <div className="relative h-52 overflow-hidden">
                <img src={p.img} alt={`${p.title} — ${p.tag} concept`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                {p.type === "video" && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-12 h-12 rounded-full bg-[#0B0C1A]/70 backdrop-blur flex items-center justify-center border border-[rgba(255,255,255,0.2)]">
                      <Play size={18} className="text-[#00D9C0] ml-0.5" />
                    </span>
                  </span>
                )}
              </div>
              <div className="p-5">
                <span className="text-xs font-heading font-semibold tracking-widest uppercase text-[#6C5CE7]">{p.tag}</span>
                <h3 className="text-[1.05rem] mt-1.5 m-0">{p.title}</h3>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ReviewsSection = ({ chapter = "06" }) => (
  <section className="py-24 bg-[#1B1D33]/40 border-y border-[rgba(255,255,255,0.05)]" data-testid="reviews-section">
    <div className="container-x max-w-3xl mx-auto text-center">
      <Reveal>
        <p className="eyebrow mb-3">{chapter} — Reviews</p>
        <h2>Just getting started — and glad to prove it.</h2>
        <p className="text-[#A0A3C2] mt-6 leading-relaxed">
          We're a new studio, so we don't have reviews yet — and we'd rather show you that
          honestly than fake it. Message us on WhatsApp, be one of our first clients, and
          your project (and your words) could be featured right here.
        </p>
        <div className="flex flex-wrap justify-center gap-4 mt-9">
          <a href={BUSINESS.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="reviews-whatsapp-btn" className="btn btn-wa no-underline">
            <MessageCircle size={17} /> Be our first featured client
          </a>
        </div>
        <div className="mt-12 p-8 rounded-xl border border-dashed border-[rgba(160,163,194,0.3)]" data-testid="google-reviews-placeholder">
          <Star size={22} className="text-[#A0A3C2] mx-auto mb-3" />
         
        </div>
      </Reveal>
    </div>
  </section>
);

export const ServiceArea = ({ chapter = "07" }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="py-24" data-testid="service-area-section">
      <div className="container-x">
        <Reveal>
          <p className="eyebrow mb-3">{chapter} — Where we work</p>
          <h2 className="max-w-2xl">One studio. Clients everywhere.</h2>
          <p className="text-[#A0A3C2] mt-4 max-w-xl">
            Fully remote, timezone-flexible, and already set up to serve clients across four continents.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 card p-6 sm:p-10 hover:!transform-none">
            <WorldMap />
            <button
              data-testid="country-list-toggle"
              onClick={() => setOpen(!open)}
              className="btn btn-ghost mt-6 text-sm"
              aria-expanded={open}
            >
              {open ? "Hide" : "See"} the full country list
              <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <ul data-testid="country-list" className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 mt-6 p-0 list-none">
                {COUNTRIES.map((c) => (
                  <li key={c.name} className="text-[#A0A3C2] text-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00D9C0]" /> {c.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const FAQSection = ({ chapter = "08", limit }) => (
  <section className="py-24" data-testid="faq-section">
    <div className="container-x max-w-3xl">
      <Reveal>
        <p className="eyebrow mb-3">{chapter} — FAQ</p>
        <h2>Questions, answered straight.</h2>
      </Reveal>
      <Reveal delay={0.15}>
        <Accordion type="single" collapsible className="mt-10">
          {(limit ? FAQS.slice(0, limit) : FAQS).map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-[rgba(255,255,255,0.08)]">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-heading font-semibold text-[#F5F5FA] hover:text-[#00D9C0] hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#A0A3C2] leading-relaxed text-[0.95rem]">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const FinalCTA = () => (
  <section className="py-20" data-testid="final-cta-section">
    <div className="container-x">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl p-10 sm:p-16 text-center" style={{ background: "linear-gradient(115deg, #6C5CE7 0%, #4a3fd1 45%, #00D9C0 130%)" }}>
          <h2 className="text-white">Ready to start?</h2>
          <p className="text-white/85 max-w-xl mx-auto mt-4">
            Tell us what you're building. We'll reply on WhatsApp — usually the same day.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-9">
            <a href={BUSINESS.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp-btn" className="btn btn-wa no-underline">
              <MessageCircle size={17} /> Chat on WhatsApp
            </a>
            <Link to="/contact" data-testid="cta-quote-btn" className="btn no-underline bg-white/12 text-white border border-white/35 hover:bg-white/20">
              Request a quote
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const ContactBlock = ({ chapter = "09" }) => (
  <section className="py-24 bg-[#1B1D33]/40 border-y border-[rgba(255,255,255,0.05)]" data-testid="contact-block">
    <div className="container-x grid lg:grid-cols-5 gap-14">
      <div className="lg:col-span-2">
        <Reveal>
          <p className="eyebrow mb-3">{chapter} — Contact</p>
          <h2>Get a quote.</h2>
          <p className="text-[#A0A3C2] mt-4 leading-relaxed">
            Prefer to chat? WhatsApp is fastest. Otherwise, send the form and we'll come back to you by email.
          </p>
          <div className="mt-9 space-y-4 text-[0.95rem]">
            <a href={BUSINESS.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-3 text-[#F5F5FA] no-underline hover:text-[#00D9C0] transition-colors">
              <Phone size={17} className="text-[#00D9C0]" /> {BUSINESS.phoneDisplay} <span className="text-[#A0A3C2]">/ {BUSINESS.phoneIntl}</span>
            </a>
            <a href={BUSINESS.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 text-[#F5F5FA] no-underline hover:text-[#25D366] transition-colors">
              <MessageCircle size={17} className="text-[#25D366]" /> WhatsApp: {BUSINESS.phoneIntl}
            </a>
            <p className="flex items-start gap-3 text-[#A0A3C2] m-0">
              <Clock size={17} className="text-[#00D9C0] mt-0.5 shrink-0" /> {BUSINESS.hours}
            </p>
            <p className="text-[#A0A3C2] m-0 pl-8">
              {BUSINESS.social.handle} — Facebook · Instagram · TikTok
            </p>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-3">
        <Reveal delay={0.15}>
          <div className="card p-7 sm:p-9 hover:!transform-none">
            <QuoteForm />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
